import { hasDefinedValue, isNonEmpty } from '@sgme/fp';
import { FilterModel } from 'ag-grid-community';
import { isMultiFilter, isSimpleFilter } from 'components/Blotters/Filter/Badges/type-guards';
import type { ExtractedFilter } from 'components/Blotters/Filter/utils';
import { getFilterValues } from 'components/Blotters/Filter/utils';
import type { ColumnPreferenceState } from 'components/shared/Blotter/Grid/blotter.model';
import { useSPIntl } from 'hooks/useIntl';
import type { IntlKey } from 'locales';
import { useCallback, useMemo } from 'react';
import type { IBlotterColumnDefinition } from 'state/blotters/common/blotter.model';
import { ObjectWithDefinedValue } from 'types/object';
import { getFrontLogger } from 'utils/loggers/FrontLogger';
export type Filter = { colId: string; values: ExtractedFilter[]; isBooleanExpression: boolean };

export function useClientSideGridSummary(
  columns: readonly ColumnPreferenceState[] = emptyColumns,
): [Filter[], ObjectWithDefinedValue<ColumnPreferenceState, 'sort'>[]] {
  const filters = useGetFilterValues(columns);
  const sortCols = columns.filter(hasSortProperty);
  return [filters, sortCols];
}

export function useServerSideGridSummary(
  columns: readonly ColumnPreferenceState[] = emptyColumns,
): [Filter[], ObjectWithDefinedValue<ColumnPreferenceState, 'sort'>[]] {
  const filters = columns
    .map(f => [f.colId, f.filterModel] as [string, string])
    .filter(([, filterModel]) => filterModel)
    ?.map(([colId, filterModel]) => {
      const parsedFilterModel = parseFilterModel(filterModel);
      return { colId, values: extractFilter(parsedFilterModel), isBooleanExpression: false } satisfies Filter;
    });
  const sortCols = columns.filter(hasSortProperty);
  return [filters, sortCols];
}

export function parseFilterModel(filterModel: unknown): FilterModel | undefined {
  if (typeof filterModel === 'string') {
    try {
      return JSON.parse(filterModel);
    } catch {
      getFrontLogger().warn('Failed to parse JSON string in filter model:', filterModel);
      return undefined;
    }
  }
  if (typeof filterModel === 'object') {
    if (isMultiFilter(filterModel)) {
      return filterModel.filterModels?.[0];
    }
    if (isSimpleFilter(filterModel)) {
      return filterModel;
    }
  }
}

function extractFilter(filter: FilterModel | undefined): ExtractedFilter[] {
  switch (filter?.filterType) {
    case 'text':
      return [{ type: 'text', value: filter.filter ?? '', comparisonOperator: filter.type }];
    case 'number':
      return [{ type: 'number', value: filter.filter, comparisonOperator: filter.type }];
    case 'set':
      return [{ type: 'set', value: filter.values?.join(', '), comparisonOperator: 'equals' }];
    case 'date':
      return [{ type: 'date', value: filter.dateFrom ?? '', comparisonOperator: filter.type }];
    default:
      return [];
  }
}

const emptyColumns: readonly [] = [];

const hasSortProperty = hasDefinedValue('sort');

const useGetFilterValues = (columns: readonly Pick<ColumnPreferenceState, 'filterModel' | 'filter' | 'colId'>[]) => {
  const { formatDisplayedDate } = useSPIntl();
  return useMemo(
    () =>
      columns
        .filter(c => !!c.filter || !!c.filterModel)
        .map((f): Filter => {
          const values = getFilterValues(f);
          return {
            values: values.map(filterValue => {
              if (filterValue.type === 'date') {
                return { ...filterValue, value: formatDisplayedDate(filterValue.value) };
              } else {
                return filterValue;
              }
            }),
            isBooleanExpression: values.some(v => !!v.boolOperator),
            colId: f.colId,
          };
        })
        .filter(f => isNonEmpty(f.values)),
    [columns, formatDisplayedDate],
  );
};

export const useHeaderTranslationById = (
  translationFunc: (colId: string) => IntlKey,
  definitions?: readonly IBlotterColumnDefinition[] | null,
) => {
  return useCallback(
    (colId: string) => {
      const foundId = translationFunc(definitions?.find(d => d.field === colId)?.headerName ?? colId);
      return foundId;
    },
    [definitions, translationFunc],
  );
};

export const useHasSort = (columns: readonly Pick<ColumnPreferenceState, 'sort'>[]) => {
  return useMemo(() => columns.some(c => c.sort), [columns]);
};
